import { afterNextRender, DestroyRef, Directive, ElementRef, inject, signal } from '@angular/core';

/**
 * Applied to a horizontally-scrolling container: exposes `canScroll` (true once the content
 * overflows) and `scrollRight()`, so consumers only need to render a chevron and wire it up.
 * Re-measures on container resize AND on content changes (items added/removed), which a plain
 * ResizeObserver on the container would otherwise miss.
 */
@Directive({
  selector: '[rmaOverflowScroll]',
  exportAs: 'rmaOverflowScroll',
})
export class OverflowScrollDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly canScroll = signal(false);

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  constructor() {
    const destroyRef = inject(DestroyRef);

    // afterNextRender only runs in the browser, so the observers stay SSR-safe.
    afterNextRender(() => {
      const el = this.host.nativeElement;
      const measure = () => this.canScroll.set(el.scrollWidth - el.clientWidth > 1);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(measure);
        this.observeChildren();
      }

      if (typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(() => {
          this.observeChildren();
          measure();
        });
        this.mutationObserver.observe(el, { childList: true, subtree: true });
      }

      measure();
    });

    destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
    });
  }

  public scrollRight(): void {
    const el = this.host.nativeElement;
    const containerLeft = el.getBoundingClientRect().left;

    // Item left edges relative to the current left edge; keep only those still to the right.
    const offsets = this.items()
      .map((item) => item.getBoundingClientRect().left - containerLeft)
      .filter((offset) => offset > 1);

    if (!offsets.length) {
      return;
    }

    // Advance roughly a page but land on an item boundary so nothing is cut in half.
    const withinPage = offsets.filter((offset) => offset <= el.clientWidth + 1);
    const delta = withinPage.length ? withinPage[withinPage.length - 1] : offsets[0];
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  private items(): HTMLElement[] {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const children = Array.from(this.host.nativeElement.children) as HTMLElement[];

    // Accolade pills are wrapped in a single flex row; descend so we measure the real items.
    if (children.length === 1 && children[0].children.length > 0) {
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      return Array.from(children[0].children) as HTMLElement[];
    }

    return children;
  }

  private observeChildren(): void {
    const el = this.host.nativeElement;
    this.resizeObserver?.observe(el);
    for (const child of Array.from(el.children)) {
      this.resizeObserver?.observe(child);
    }
  }
}
